import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useBreadcrumb } from "./useBreadcrumb";
import useMenuItems from "./useMenuItems";

export interface TrailEntry { path: string; label: string; }

// Stored when the user jumps from one top-level menu section to another.
// Cleared when they navigate back to the origin section or click the back link.
interface CrossSectionBack {
  path: string;       // the page to navigate back to
  label: string;      // human-readable label for the back link
  fromSection: string; // the section label we came FROM (used to auto-clear on return)
}

const BACK_KEY = "v-cross-back";

function readStoredBack(): CrossSectionBack | null {
  try {
    const raw = window.sessionStorage.getItem(BACK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.path !== "string") return null;
    return parsed as CrossSectionBack;
  } catch {
    return null;
  }
}

function writeStoredBack(val: CrossSectionBack | null) {
  try {
    if (val === null) window.sessionStorage.removeItem(BACK_KEY);
    else window.sessionStorage.setItem(BACK_KEY, JSON.stringify(val));
  } catch {
    // sessionStorage unavailable (private mode / quota)
  }
}

function getTopSection(
  menuItems: ReturnType<typeof useMenuItems>,
  path: string
): string | null {
  for (const item of menuItems) {
    if (!item.enabled) continue;
    if (item.sublinks) {
      if (item.sublinks.some(s => s.enabled && (path === s.to || path.startsWith(s.to + "/")))) {
        return item.label;
      }
    } else if (item.to && (path === item.to || path.startsWith(item.to + "/"))) {
      return item.label;
    }
  }
  return null;
}

/**
 * Powers the two-layer navigation affordance:
 *
 *   Layer 1 — Hierarchical breadcrumb (always):
 *     Derived from the menu tree. Shows where the current page sits in the
 *     navigation structure, e.g. "Messaging › My Workflows". Never history-based.
 *
 *   Layer 2 — Cross-section back link (only when applicable):
 *     When the user jumps from one top-level section to another (e.g., clicking
 *     a link on Visitor Monitor that opens My Workflows), a "← Back to Visitor Monitor"
 *     link is surfaced so they can return without hunting through the sidebar.
 *     Cleared automatically when they navigate back to the origin section, or when
 *     they explicitly click the back link.
 */
export function useNavigationTrail() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuBreadcrumb = useBreadcrumb();
  const menuItems = useMenuItems();

  const [crossSectionBack, setCrossSectionBack] = useState<CrossSectionBack | null>(
    () => readStoredBack()
  );

  // Refs track the previous page so we can compute cross-section jumps.
  const prevPathRef = useRef<string | null>(null);
  const prevLabelRef = useRef<string | null>(null);
  const prevSectionRef = useRef<string | null>(null);
  // Prevents double-firing on StrictMode double-invoke
  const lastProcessedPathRef = useRef<string | null>(null);

  const currentSection = useMemo(
    () => getTopSection(menuItems, location.pathname),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, menuItems]
  );

  const friendlyLabel = useMemo(() => {
    const last = menuBreadcrumb[menuBreadcrumb.length - 1];
    if (last?.label) return last.label;
    const seg = location.pathname.split("/").filter(Boolean).pop() ?? "";
    return (
      seg
        .split(/[-_]/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ") || "Home"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuBreadcrumb]);

  useEffect(() => {
    const path = location.pathname;
    if (lastProcessedPathRef.current === path) return;
    lastProcessedPathRef.current = path;

    const prevPath = prevPathRef.current;
    const prevSection = prevSectionRef.current;

    setCrossSectionBack(prev => {
      // Auto-clear: user navigated back to the section the referrer came from
      if (prev && currentSection === prev.fromSection) {
        writeStoredBack(null);
        return null;
      }

      // Cross-section jump: previous and current pages are in different known sections
      if (prevPath && prevSection && currentSection && prevSection !== currentSection) {
        const newBack: CrossSectionBack = {
          path: prevPath,
          label: prevLabelRef.current ?? "Previous page",
          fromSection: prevSection,
        };
        writeStoredBack(newBack);
        return newBack;
      }

      return prev;
    });

    prevSectionRef.current = currentSection;
    prevPathRef.current = path;
    prevLabelRef.current = friendlyLabel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentSection, friendlyLabel]);

  function goBack() {
    if (crossSectionBack) {
      const dest = crossSectionBack.path;
      setCrossSectionBack(null);
      writeStoredBack(null);
      navigate(dest);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }

  // trail — the hierarchical menu-based breadcrumbs, repackaged as TrailEntry[]
  // for backwards-compat with callers that read trail.length / trail[i].label
  const trail: TrailEntry[] = menuBreadcrumb.map(c => ({
    path: c.to ?? location.pathname,
    label: c.label,
  }));

  return {
    trail,
    breadcrumbs: menuBreadcrumb,
    crossSectionBack,
    goBack,
    current: trail[trail.length - 1] ?? { path: location.pathname, label: friendlyLabel },
  };
}
